import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Crop, Loader2, Sparkles, Wand2, X } from "lucide-react";
import { offerApi } from "../../../services/offerApi";
import { useAssetUrl } from "./assets";

// Outils d'une image sélectionnée dans l'éditeur, comme sur Guttenberg : « Fond pro » (toujours le même fond blanc
// cassé uni), retouche décrite en une phrase (modèle d'image d'OpenAI, 20 à 60 s) et recadrage (dans le navigateur).
// Chaque outil enregistre une nouvelle image et la met à la place : l'originale reste, Ctrl+Z y revient.

const pill = "inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-xs font-medium text-slate-700 shadow ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50";

const toBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
  reader.onerror = () => reject(new Error("Lecture de l'image impossible."));
  reader.readAsDataURL(blob);
});

// Recadrage : on trace le cadre à la souris sur l'image, découpé en pixels réels dans un canvas.
const CropDialog = ({ url, onCrop, onClose }) => {
  const imageRef = useRef(null);
  const [box, setBox] = useState(null);
  const [start, setStart] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const point = (event) => {
    const rect = imageRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
  };
  const move = (event) => {
    if (!start) return;
    const end = point(event);
    setBox({ x: Math.min(start.x, end.x), y: Math.min(start.y, end.y), w: Math.abs(end.x - start.x), h: Math.abs(end.y - start.y) });
  };
  const confirm = async () => {
    const image = imageRef.current;
    const width = Math.round(box.w * image.naturalWidth);
    const height = Math.round(box.h * image.naturalHeight);
    if (width < 16 || height < 16) { setError("Cadre trop petit."); return; }
    setBusy(true);
    setError("");
    try {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(image, Math.round(box.x * image.naturalWidth), Math.round(box.y * image.naturalHeight),
        width, height, 0, 0, width, height);
      let blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (blob.size > 5 * 1024 * 1024) blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
      const result = await offerApi.uploadAsset({ filename: `recadrage.${blob.type === "image/png" ? "png" : "jpg"}`,
        content_type: blob.type, data: await toBase64(blob) });
      onCrop(result.ref);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60 p-6" onClick={onClose}>
      <div className="flex max-h-full max-w-5xl flex-col gap-3 rounded-xl bg-white p-4 shadow-xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-sm font-semibold text-slate-800">Recadrer : tracez le cadre à garder sur l'image</h2>
          <button type="button" aria-label="Fermer" onClick={onClose} className="rounded-full p-1 text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="relative select-none self-center" onMouseMove={move} onMouseUp={() => setStart(null)} onMouseLeave={() => setStart(null)}>
          <img ref={imageRef} src={url} alt="" draggable={false} className="block max-h-[70vh] max-w-full cursor-crosshair"
            onMouseDown={(event) => { event.preventDefault(); const p = point(event); setStart(p); setBox({ ...p, w: 0, h: 0 }); }} />
          {box && box.w > 0 && (
            <div className="pointer-events-none absolute border-2 border-white"
              style={{ left: `${box.x * 100}%`, top: `${box.y * 100}%`, width: `${box.w * 100}%`, height: `${box.h * 100}%`,
                boxShadow: "0 0 0 9999px rgba(15,23,42,.55)" }} />
          )}
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100">Annuler</button>
          <button type="button" disabled={!box || box.w < 0.02 || box.h < 0.02 || busy} onClick={confirm}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-sm text-white disabled:opacity-40">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crop className="h-4 w-4" />} Recadrer
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Posé sur l'image (position absolue) : `asset` est l'image affichée, `onReplace(ref)` la remplace dans le document.
const ImageTools = ({ asset, onReplace }) => {
  const url = useAssetUrl(asset);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [asking, setAsking] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [cropping, setCropping] = useState(false);
  const assetId = asset?.slice(6);

  const retouch = async (mode, text) => {
    setBusy(mode === "background" ? "Fond pro en cours…" : "Retouche en cours…");
    setError("");
    setAsking(false);
    try {
      const result = await offerApi.retouchAsset(assetId, { mode, instruction: text || null });
      onReplace(result.ref);
      setInstruction("");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };

  const stop = (event) => event.stopPropagation();
  return (
    <>
      {busy && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60" onClick={stop}>
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow">
            <Loader2 className="h-4 w-4 animate-spin" /> {busy} (jusqu'à une minute)
          </span>
        </div>
      )}
      <div className="absolute right-2 top-2 z-20 flex max-w-[90%] flex-col items-end gap-1.5" onClick={stop}>
        <div className="flex flex-wrap justify-end gap-1.5">
          <button type="button" className={pill} disabled={Boolean(busy)} onClick={() => retouch("background")}
            title="Remplace l'arrière-plan par le fond blanc cassé uni des rapports, sans toucher au sujet">
            <Sparkles className="h-3.5 w-3.5 text-blue-600" /> Fond pro
          </button>
          <button type="button" className={pill} disabled={Boolean(busy)} onClick={() => setAsking(!asking)}
            title="Décrire la retouche voulue en une phrase">
            <Wand2 className="h-3.5 w-3.5 text-blue-600" /> Retoucher
          </button>
          <button type="button" className={pill} disabled={Boolean(busy) || !url} onClick={() => setCropping(true)} title="Recadrer l'image">
            <Crop className="h-3.5 w-3.5 text-blue-600" /> Recadrer
          </button>
        </div>
        {asking && (
          <form className="w-72 space-y-2 rounded-lg bg-white p-2 shadow-lg ring-1 ring-slate-200"
            onSubmit={(event) => { event.preventDefault(); if (instruction.trim()) retouch("edit", instruction.trim()); }}>
            <textarea autoFocus rows={3} value={instruction} onChange={(event) => setInstruction(event.target.value)} maxLength={1000}
              placeholder="Ex. retirer le câble rouge à gauche, éclaircir la photo"
              className="w-full resize-none rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-800 focus:border-blue-400 focus:outline-none" />
            <div className="flex justify-end gap-1.5">
              <button type="button" onClick={() => setAsking(false)} className="rounded-full px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" disabled={!instruction.trim()} className="rounded-full bg-slate-900 px-2.5 py-1 text-xs text-white disabled:opacity-40">Retoucher</button>
            </div>
          </form>
        )}
        {error && <p className="max-w-xs rounded-md bg-white px-2 py-1 text-xs text-red-600 shadow">{error}</p>}
      </div>
      {cropping && url && (
        <CropDialog url={url} onClose={() => setCropping(false)} onCrop={(ref) => { setCropping(false); onReplace(ref); }} />
      )}
    </>
  );
};

export default ImageTools;
